import React, { useState, useRef } from 'react';
import emailjs from '@emailjs/browser';

const Contact = () => {
  const form = useRef();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  
  const [errors, setErrors] = useState({});
  const [submitStatus, setSubmitStatus] = useState(null);

  // EmailJS configuration - you'll need to replace these with your actual values
  const SERVICE_ID = "service_ctoqtx3";
  const TEMPLATE_ID = "template_y6d1c04";
  const PUBLIC_KEY = "crehwTcIzoEjzPDfW";

  // Initialize EmailJS
  emailjs.init(PUBLIC_KEY);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!formData.message.trim()) newErrors.message = "Message is required";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      // Set loading state
      setSubmitStatus('loading');
      
      // Send email through EmailJS
      emailjs.sendForm(SERVICE_ID, TEMPLATE_ID, form.current)
        .then((result) => {
          console.log('Email successfully sent!', result.text);
          setSubmitStatus('success');
          // Reset form
          setFormData({
            name: '',
            email: '',
            subject: '',
            message: ''
          });
          // Reset form after 3 seconds
          setTimeout(() => setSubmitStatus(null), 3000);
        }, (error) => {
          console.log('Failed to send email:', error.text);
          setSubmitStatus('error');
          setTimeout(() => setSubmitStatus(null), 3000);
        });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 to-slate-900 pt-20">
      {/* Header section */}
      <div className="container mx-auto px-4 py-12">
        <div className="relative mb-12 text-center">
          <div className="absolute left-1/2 top-1/2 -z-10 h-36 w-36 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-br from-cyan-500/20 to-blue-500/0 blur-2xl" />
          <h1 className="relative text-4xl font-bold text-white md:text-5xl lg:text-6xl">
            Get in <span className="bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent">Touch</span>
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-400">
            Have a project in mind? Let's discuss how I can help elevate your digital presence.
          </p>
        </div>

        <div className="flex flex-col gap-12 lg:flex-row">
          {/* Contact Information */}
          <div className="w-full lg:w-1/3">
            <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6">
              <h2 className="mb-6 text-2xl font-bold text-white">Contact Information</h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-cyan-500/20 to-blue-500/20">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5 text-cyan-500">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-md font-semibold text-white">Email</h3>
                    <p className="text-slate-400">info@forca.dev</p>
                    <a href="mailto:hello@wesleyscales.com" className="mt-1 inline-block text-sm text-cyan-500 hover:text-cyan-400">
                      Send an email
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-cyan-500/20 to-blue-500/20">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5 text-cyan-500">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-md font-semibold text-white">Telegram</h3>
                    <p className="text-slate-400">@wesley676</p>
                    <a href="https://t.me/wesley676" className="mt-1 inline-block text-sm text-cyan-500 hover:text-cyan-400">
                      Message on Telegram
                    </a>
                  </div>
                </div>
              </div>
              
              <div className="mt-8 pt-6 border-t border-slate-800/30">
                <p className="text-sm text-slate-400">I'll get back to you as soon as possible. Typically within 24 hours on business days.</p>
              </div>
            </div>
          </div>
          
          {/* Contact Form */}
          <div className="w-full lg:w-2/3">
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-b from-slate-950 to-slate-900 p-[1px] shadow-2xl">
              <div className="absolute inset-0 bg-gradient-to-b from-cyan-500 to-blue-500 opacity-10" />
              <div className="relative rounded-2xl bg-gradient-to-b from-slate-950 to-slate-900 p-8">
                <div className="absolute -left-16 -top-16 h-64 w-64 rounded-full bg-gradient-to-br from-cyan-500/20 to-transparent blur-2xl"></div>
                <div className="absolute -bottom-16 -right-16 h-64 w-64 rounded-full bg-gradient-to-br from-blue-500/20 to-transparent blur-2xl"></div>
                
                <h2 className="mb-6 text-2xl font-bold text-white">Send me a message</h2>
                
                <form ref={form} onSubmit={handleSubmit}>
                  <div className="mb-6 grid gap-6 md:grid-cols-2">
                    <div>
                      <label htmlFor="name" className="mb-2 block text-sm font-medium text-white">
                        Your Name
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          className={`block w-full rounded-lg border ${errors.name ? 'border-red-500' : 'border-slate-700'} bg-slate-800/50 p-3 text-white focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500`}
                          placeholder="John Doe"
                        />
                        {errors.name && (
                          <p className="mt-1 text-xs text-red-500">{errors.name}</p>
                        )}
                      </div>
                    </div>
                    <div>
                      <label htmlFor="email" className="mb-2 block text-sm font-medium text-white">
                        Your Email
                      </label>
                      <div className="relative">
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className={`block w-full rounded-lg border ${errors.email ? 'border-red-500' : 'border-slate-700'} bg-slate-800/50 p-3 text-white focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500`}
                          placeholder="john@example.com"
                        />
                        {errors.email && (
                          <p className="mt-1 text-xs text-red-500">{errors.email}</p>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="mb-6">
                    <label htmlFor="subject" className="mb-2 block text-sm font-medium text-white">
                      Subject
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="block w-full rounded-lg border border-slate-700 bg-slate-800/50 p-3 text-white focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
                      placeholder="How can I help you?"
                    />
                  </div>
                  
                  <div className="mb-6">
                    <label htmlFor="message" className="mb-2 block text-sm font-medium text-white">
                      Your Message
                    </label>
                    <div className="relative">
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows="6"
                        className={`block w-full rounded-lg border ${errors.message ? 'border-red-500' : 'border-slate-700'} bg-slate-800/50 p-3 text-white focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500`}
                        placeholder="Let me know how I can help you with your project..."
                      ></textarea>
                      {errors.message && (
                        <p className="mt-1 text-xs text-red-500">{errors.message}</p>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <button
                      type="submit"
                      disabled={submitStatus === 'loading'}
                      className="group relative overflow-hidden rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 p-px font-semibold text-white shadow-[0_1000px_0_0_hsl(0_0%_100%_/_0%)_inset] transition-colors hover:shadow-[0_1000px_0_0_hsl(0_0%_100%_/_2%)_inset]"
                    >
                      <div className="relative rounded-xl bg-slate-950/50 px-6 py-3 transition-colors group-hover:bg-transparent">
                        <span className="relative flex items-center justify-center gap-2">
                          {submitStatus === 'loading' ? (
                            <>
                              <svg className="h-5 w-5 animate-spin text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Sending...
                            </>
                          ) : submitStatus === 'success' ? (
                            <>
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              Message Sent!
                            </>
                          ) : submitStatus === 'error' ? (
                            <>
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                              </svg>
                              Error sending message. Please try again.
                            </>
                          ) : (
                            <>
                              Send Message
                              <svg stroke="currentColor" viewBox="0 0 24 24" fill="none" className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1">
                                <path d="M17 8l4 4m0 0l-4 4m4-4H3" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
                              </svg>
                            </>
                          )}
                        </span>
                      </div>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
